import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/feature/Reveal';
import { testimonials } from '@/mocks/testimonials';

/**
 * Two proof points chosen specifically for the claim experience.
 *
 * The homepage testimonials speak to results; these two instead cover what a
 * visitor on this page is actually nervous about — that the preset really fits
 * their account, and that "free" still comes with real human support. Drawn
 * from the shared test data so the wording stays consistent site-wide.
 */
const PICKED = ['aisha', 'kenji'];

function Stars({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, index) => (
        <i
          key={index}
          className={
            index < rating ? 'ri-star-fill text-sm text-accent-500' : 'ri-star-line text-sm text-background-300'
          }
          aria-hidden="true"
        ></i>
      ))}
    </div>
  );
}

export default function SocialProof() {
  const quoted = testimonials.filter((item) => PICKED.includes(item.id));

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-shell mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="In their words"
              title="People who claimed it, then kept it"
              subtitle="Claiming is the easy part. These two stuck around because the preset fitted and the chat actually helped."
            />
          </Reveal>

          <div className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {quoted.map((item, index) => (
              <Reveal key={item.id} delay={index * 90}>
                <figure className="h-full flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 md:p-6 hover:border-accent-300 transition-colors duration-200">
                  <div className="flex items-center justify-between">
                    <Stars rating={item.rating} />
                    <i className="ri-double-quotes-r text-accent-300 text-2xl" aria-hidden="true"></i>
                  </div>
                  <blockquote className="mt-4 flex-1 text-xs md:text-sm leading-relaxed text-foreground-700">
                    {item.quote}
                  </blockquote>
                  <figcaption className="mt-5 pt-5 border-t border-background-200 flex items-center gap-3">
                    <span className="w-11 h-11 shrink-0 rounded-full overflow-hidden border border-background-200">
                      <img
                        src={item.avatar}
                        alt={`${item.name}, ${item.role}`}
                        title={`${item.name} — ${item.role}, GoldWarden EA user`}
                        className="w-full h-full object-cover object-top"
                      />
                    </span>
                    <div className="min-w-0">
                      <p className="font-heading text-sm font-semibold text-foreground-950 truncate">{item.name}</p>
                      <p className="text-xs text-foreground-500 truncate">
                        {item.role} · {item.location}
                      </p>
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}